import { EnhancedDomain } from 'element-domain/'
import { createRecipe } from 'element-domain/src/__generated/implementation'
import type { BaseSingleLeg } from 'element-domain/src/__generated/model'
import {
  Base,
  LeafTypes,
  Table,
  Top,
  TopWithDiameter,
  TopWithWidthAndLength,
} from 'element-domain/src/__generated/model'
import { DOUBLE_BASE_ALTERNATIVE_MAP, PLATFORM_BASE_ALTERNATIVE_MAP } from 'element-domain/src/data/base-data'
import { SEATING_MAP } from 'element-domain/src/data/table-data'
import { THICKNESS_OPTIONS } from 'element-domain/src/data/top-data'

import { configuratorInterface } from './configurator-interface'

const deselectTransformer = configuratorInterface.transformerBuilder.when.navigated().then.deselect().build('deselect')

const deselectOnEntityCreationTransformer = configuratorInterface.transformerBuilder.when
  .entity(EnhancedDomain.$AnyDomainEntity.types)
  .created()
  .then.deselect()
  .build('deselect-on-entity-creation')

const thicknessNotAvailableForThisTopModifiedTransformer = configuratorInterface.transformerBuilder.when
  .entity(Top.types)
  .modified({ atFields: ['thicknessType'] })
  .then.applySideEffect(ctx => {
    const newEntity = ctx.getNew$Entity()

    return [
      configuratorInterface.updates.DomainEntityModification({
        entityId: newEntity.id,
        stateDelta: {
          thickness: THICKNESS_OPTIONS[newEntity.thicknessType].defaultValue,
        },
        updateTag: 'fix.unlockThicknessOptions',
      }),
    ]
  })
  .build('thickness-not-available-for-this-top-modified')

const baseTypeChangedTransformer = configuratorInterface.transformerBuilder.when
  .entity([Table.type])
  .modified({ atFields: ['baseType'] })
  .then.applySideEffect(ctx => {
    const newTable = ctx.getNew$Entity()
    const currentBase = newTable.$base
    const currentBaseType = currentBase.$baseDataMap[currentBase.type].baseType

    // When baseType changes, current base may be wrong category (e.g. Pedestal base when Metal selected)
    if (currentBaseType !== newTable.baseType) {
      const compatibleBases = Base.types.filter(
        baseStyle =>
          currentBase.$baseDataMap[baseStyle].baseType === newTable.baseType &&
          newTable.$top.$availableBaseStyles.includes(baseStyle)
      )
      const defaultBase = compatibleBases[0]
      if (defaultBase) {
        const targetSupportsCrossBrace = currentBase.$baseDataMap[defaultBase].supportsCrossBrace
        return [
          configuratorInterface.updates.DomainEntityReplacement({
            entityId: currentBase.id,
            replacementEntity: {
              ...createRecipe({
                type: defaultBase,
                hasCrossbrace: targetSupportsCrossBrace ? currentBase.hasCrossbrace : false,
                finish: currentBase.finish,
              }),
            },
            keepChildren: true,
            updateTag: 'fix.baseTypeChanged',
          }),
        ]
      }
    }
    return []
  })
  .build('base-type-changed')

const baseStyleNotAvailableForThisTopTransformer = configuratorInterface.transformerBuilder.when
  .entity(Top.types)
  .replaces(Top.types)
  .causes.self.violation(['baseStyleNotAvailableForThisTop'])
  .then.applySideEffect(ctx => {
    const replacingEntity = ctx.getReplacing$Entity()
    const oldRoot = ctx.getOld$Root()
    const newRoot = ctx.getNew$Root()
    const oldBase = oldRoot.$base

    let preferredBase: Base['type'] | null = null
    if (EnhancedDomain.$BaseDoubleLeg.is(oldBase)) {
      const singleCandidate = oldBase.$singleLegAlternative as Base['type']
      if (replacingEntity.$availableBaseStyles.includes(singleCandidate)) {
        preferredBase = singleCandidate
      }
    } else if (EnhancedDomain.$BaseSingleLeg.is(oldBase)) {
      const doubleBaseAlternativeMap = { ...DOUBLE_BASE_ALTERNATIVE_MAP, ...PLATFORM_BASE_ALTERNATIVE_MAP }
      const doubleCandidate = doubleBaseAlternativeMap[oldBase.type as BaseSingleLeg['type']]
      if (doubleCandidate && replacingEntity.$availableBaseStyles.includes(doubleCandidate)) {
        preferredBase = doubleCandidate as Base['type']
      }
    }

    const baseReplacement = preferredBase ?? (replacingEntity.$availableBaseStyles[0]! as Base['type'])
    const newBaseType = newRoot.$base.$baseDataMap[baseReplacement].baseType
    const targetSupportsCrossBrace = newRoot.$base.$baseDataMap[baseReplacement].supportsCrossBrace

    return [
      configuratorInterface.updates.DomainEntityReplacement({
        entityId: newRoot.$base.id,
        replacementEntity: {
          ...createRecipe({
            type: baseReplacement,
            hasCrossbrace: targetSupportsCrossBrace ? oldBase.hasCrossbrace : false,
            finish: oldBase.finish,
          }),
        },
        keepChildren: true,
        hint: 'The base was automatically changed to a compatible option.',
        updateTag: 'fix.baseStyleNotAvailableForThisTop',
      }),
      configuratorInterface.updates.DomainEntityModification({
        entityId: newRoot.id,
        stateDelta: {
          baseType: newBaseType,
        },
        updateTag: 'fix.baseTypeMustHaveStyles',
      }),
    ]
  })
  .build('base-style-not-available-for-this-top')

const baseStyleNotAvailableForThisTopLengthTransformer = configuratorInterface.transformerBuilder.when
  .entity(TopWithWidthAndLength.types)
  .modified({ atFields: ['length'] })
  .causes.self.violation(['baseStyleNotAvailableForThisTop'])
  .then.applySideEffect(ctx => {
    const oldRoot = ctx.getOld$Root()
    const newRoot = ctx.getNew$Root()
    const oldBase = oldRoot.$base
    const doubleBaseAlternativeMap = { ...DOUBLE_BASE_ALTERNATIVE_MAP, ...PLATFORM_BASE_ALTERNATIVE_MAP }
    const doubleCandidate = doubleBaseAlternativeMap[oldBase.type as BaseSingleLeg['type']]

    let baseReplacement: Base['type'] | null = null
    if (
      EnhancedDomain.$RogersBase.is(oldBase) &&
      newRoot.$top.$length >= newRoot.$top.$minTopLengthForDoubleBase &&
      newRoot.$top.$availableBaseStyles.includes(oldBase.$singleLegAlternative)
    ) {
      baseReplacement = oldBase.$singleLegAlternative as Base['type']
    } else if (doubleCandidate && newRoot.$top.$availableBaseStyles.includes(doubleCandidate)) {
      baseReplacement = doubleCandidate as Base['type']
    }

    if (!baseReplacement) {
      return []
    }

    const newBaseType = newRoot.$base.$baseDataMap[baseReplacement].baseType
    const targetSupportsCrossBrace = newRoot.$base.$baseDataMap[baseReplacement].supportsCrossBrace

    return [
      configuratorInterface.updates.DomainEntityReplacement({
        entityId: oldRoot.$base.id,
        replacementEntity: {
          ...createRecipe({
            type: baseReplacement,
            hasCrossbrace: targetSupportsCrossBrace ? oldBase.hasCrossbrace : false,
            finish: oldBase.finish,
          }),
        },
        keepChildren: true,
        hint: 'The base was automatically changed to a compatible option.',
        updateTag: 'fix.baseStyleNotAvailableForThisTop',
      }),
      configuratorInterface.updates.DomainEntityModification({
        entityId: oldRoot.id,
        stateDelta: {
          baseType: newBaseType,
        },
        updateTag: 'fix.baseTypeMustHaveStyles',
      }),
    ]
  })
  .build('base-style-not-available-for-this-top-length')

const leafAmountNotAvailableForThisLeafTypeModifiedTransformer = configuratorInterface.transformerBuilder.when
  .entity(Top.types)
  .modified({ atFields: ['leafType'] })
  .causes.self.violation(['leafAmountNotAvailableForThisLeafType'])
  .then.applySideEffect(ctx => {
    const newEntity = ctx.getNew$Entity()
    const allowedAmounts = newEntity.$leafDataMap[newEntity.leafType]?.purchasableAmounts
    if (!allowedAmounts?.length) return []
    return [
      configuratorInterface.updates.DomainEntityModification({
        entityId: newEntity.id,
        stateDelta: {
          leafAmount: allowedAmounts[0],
        },
        updateTag: 'fix.modified.leafAmountNotAvailableForThisLeafType',
      }),
    ]
  })
  .build('leaf-amount-not-available-for-this-leaf-type-modified')

const seatingAmountNotAvailableForThisSeatingTypeModifiedTransformer = configuratorInterface.transformerBuilder.when
  .entity([Table.type])
  .modified({ atFields: ['seatingType'] })
  .causes.self.violation(['seatingAmountNotAvailableForThisSeatingType'])
  .then.applySideEffect(ctx => {
    const newEntity = ctx.getNew$Entity()
    const allowedAmounts = SEATING_MAP[newEntity.seatingType]?.purchasableAmounts
    if (!allowedAmounts?.length) return []
    return [
      configuratorInterface.updates.DomainEntityModification({
        entityId: newEntity.id,
        stateDelta: {
          seatingAmount: allowedAmounts[0],
        },
        updateTag: 'fix.modified.seatingAmountNotAvailableForThisSeatingType',
      }),
    ]
  })
  .build('seating-amount-not-available-for-this-seating-type-modified')

const lengthTooShortForDoubleBaseTransformer = configuratorInterface.transformerBuilder.when
  .entity(TopWithWidthAndLength.types)
  .modified({ atFields: ['length'] })
  .causes.self.violation(['lengthTooShortForDoubleBase'])
  .then.applySideEffect(ctx => {
    const newRoot = ctx.getNew$Root()
    const oldRoot = ctx.getOld$Root()
    const oldBase = oldRoot.$base

    let baseReplacement: Base['type'] | null = null

    if (EnhancedDomain.$BaseDoubleLeg.is(oldBase)) {
      baseReplacement = oldBase.$singleLegAlternative as Base['type']
    } else if (EnhancedDomain.$BaseSingleLeg.is(oldBase)) {
      const doubleBaseAlternativeMap = { ...DOUBLE_BASE_ALTERNATIVE_MAP, ...PLATFORM_BASE_ALTERNATIVE_MAP }
      baseReplacement = doubleBaseAlternativeMap[oldBase.type as BaseSingleLeg['type']] as Base['type']
    }

    if (!baseReplacement) {
      return []
    }

    const newBaseType = newRoot.$base.$baseDataMap[baseReplacement].baseType

    return [
      configuratorInterface.updates.DomainEntityReplacement({
        entityId: newRoot.$base.id,
        replacementEntity: {
          ...createRecipe({
            type: baseReplacement,
            hasCrossbrace: false,
            finish: oldBase.finish,
          }),
        },
        keepChildren: true,
        hint: 'The base was automatically changed to a compatible option.',
        updateTag: 'fix.lengthTooShortForDoubleBase',
      }),
      configuratorInterface.updates.DomainEntityModification({
        entityId: newRoot.id,
        stateDelta: {
          baseType: newBaseType,
        },
        updateTag: 'fix.lengthTooShortForDoubleBase',
      }),
    ]
  })
  .build('length-too-short-for-double-base')

const topWithWidthAndLengthLengthTooShortForPerimeterOrDropInLeafTransformer =
  configuratorInterface.transformerBuilder.when
    .entity(TopWithWidthAndLength.types)
    .modified({ atFields: ['length'] })
    .causes.self.violation(['lengthTooShortForPerimeterOrDropInLeaf'])
    .then.applySideEffect(ctx => {
      const newEntity = ctx.getNew$Entity()
      return [
        configuratorInterface.updates.DomainEntityModification({
          entityId: newEntity.id,
          stateDelta: {
            leafAmount: 0,
            leafType: LeafTypes.NoLeaf,
          },
          updateTag: 'fix.lengthTooShortForPerimeterOrDropInLeaf',
          hint: 'The selected length is too short for a perimeter or drop in leaf',
        }),
      ]
    })
    .build('top-with-width-and-length-length-too-short-for-perimeter-or-drop-in-leaf')

const topWithDiameterLengthTooShortForPerimeterOrDropInLeafTransformer = configuratorInterface.transformerBuilder.when
  .entity(TopWithDiameter.types)
  .modified({ atFields: ['diameter'] })
  .causes.self.violation(['lengthTooShortForPerimeterOrDropInLeaf'])
  .then.applySideEffect(ctx => {
    const newEntity = ctx.getNew$Entity()
    return [
      configuratorInterface.updates.DomainEntityModification({
        entityId: newEntity.id,
        stateDelta: {
          leafAmount: 0,
          leafType: LeafTypes.NoLeaf,
        },
        updateTag: 'fix.lengthTooShortForPerimeterOrDropInLeaf',
        hint: 'The selected length is too short for a perimeter or drop in leaf',
      }),
    ]
  })
  .build('top-with-diameter-length-too-short-for-perimeter-or-drop-in-leaf')

const baseFourLegMustHaveApronsTransformer = configuratorInterface.transformerBuilder.when
  .entity(Base.types)
  .replaces(Base.types)
  .causes.self.violation(['baseFourLegMustHaveAprons'])
  .then.applySideEffect(ctx => {
    const newRoot = ctx.getOld$Root()
    const apron = newRoot.$top.$apron

    return [
      configuratorInterface.updates.DomainEntityReplacement({
        entityId: apron.id as any,
        replacementEntity: {
          ...createRecipe({
            type: 'ApronBasic',
          }),
        },
        keepChildren: true,
        hint: 'Four leg bases must have an apron',
        updateTag: 'fix.baseFourLegMustHaveAprons',
      }),
    ]
  })
  .build('base-four-leg-must-have-aprons')

const baseTypeMustHaveStylesTransformer = configuratorInterface.transformerBuilder.when
  .entity(Top.types)
  .replaces(Top.types)
  .causes.self.violation(['baseTypeMustHaveStyles'])
  .then.applySideEffect(ctx => {
    const newRoot = ctx.getNew$Root()

    const newBaseStyle = newRoot.$top.$availableBaseStyles[0]! as Base['type']
    const newBaseType = newRoot.$base.$baseDataMap[newBaseStyle].baseType

    return [
      configuratorInterface.updates.DomainEntityModification({
        entityId: newRoot.id,
        stateDelta: {
          baseType: newBaseType,
        },
        updateTag: 'fix.baseTypeMustHaveStyles',
      }),
    ]
  })
  .build('base-type-must-have-styles')

const switchingEdgeStyleUnroundsCornersTransformer = configuratorInterface.transformerBuilder.when
  .entity(Top.types)
  .modified({ atFields: ['edgeStyle'] })
  .causes.self.violation(['roundedCorners'])
  .then.applySideEffect(ctx => {
    const newEntity = ctx.getNew$Entity()

    return [
      configuratorInterface.updates.DomainEntityModification({
        entityId: newEntity.id,
        stateDelta: {
          withCornerRadius: false,
        },
        updateTag: 'fix.switchingEdgeStyleUnroundsCorners',
      }),
    ]
  })
  .build('switching-edge-style-unrounds-corners')

export {
  deselectTransformer,
  deselectOnEntityCreationTransformer,
  baseTypeChangedTransformer,
  baseStyleNotAvailableForThisTopTransformer,
  baseStyleNotAvailableForThisTopLengthTransformer,
  leafAmountNotAvailableForThisLeafTypeModifiedTransformer,
  seatingAmountNotAvailableForThisSeatingTypeModifiedTransformer,
  thicknessNotAvailableForThisTopModifiedTransformer,
  lengthTooShortForDoubleBaseTransformer,
  topWithWidthAndLengthLengthTooShortForPerimeterOrDropInLeafTransformer,
  topWithDiameterLengthTooShortForPerimeterOrDropInLeafTransformer,
  baseFourLegMustHaveApronsTransformer,
  baseTypeMustHaveStylesTransformer,
  switchingEdgeStyleUnroundsCornersTransformer,
}
